import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Clock, RefreshCw, CheckCircle2, XCircle, Ban, Banknote,
  ClipboardList, Package, ChefHat, Mailbox, Truck, MapPin, AlertTriangle,
  CreditCard, Car, PartyPopper, Phone, Mail, Star, MessageCircle,
} from "lucide-react";
import { ordersAPI, paymentsAPI, reviewsAPI } from "../../api";
import { useAuth } from "../../context/AuthContext";
import PaymentModal from "../PaymentModal";
import ConfirmModal from "../ConfirmModal";
import ReviewForm from "../Reviews/ReviewForm";
import Hint from "../Onboarding/Hint";
import LocationDisplay from "../Location/LocationDisplay";
import "./Orders.css";

const OrderDetail = () => {
  const { orderId } = useParams();
  const { isAuthenticated, user, isBreeder, isRestaurant } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, status: null });
  const [trackingNumber, setTrackingNumber] = useState("");
  const [addingTracking, setAddingTracking] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [deliveryOTP, setDeliveryOTP] = useState("");
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [autoVerified, setAutoVerified] = useState(false);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await ordersAPI.getOne(orderId);
      setOrder(response.data);
      
      // Vérifier si un avis existe déjà pour cette commande
      if (isAuthenticated && user && response.data.status === "delivered" && response.data.buyer_id === user.id) {
        try {
          const reviewResponse = await reviewsAPI.checkForOrder(orderId);
          setExistingReview(reviewResponse.data || null);
        } catch (err) {
          // Si erreur, on continue sans l'avis existant
          console.error("Erreur lors de la vérification de l'avis:", err);
          setExistingReview(null);
        }
      } else {
        setExistingReview(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Commande non trouvée");
      } else if (err.response?.status === 403) {
        setError("Vous n'êtes pas autorisé à voir cette commande");
      } else {
        setError("Erreur lors du chargement de la commande");
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orderId, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrder();
    }
  }, [isAuthenticated, loadOrder]);

  // Réinitialiser l'auto-vérification quand on change de commande
  useEffect(() => {
    setAutoVerified(false);
  }, [orderId]);

  // Vérification automatique au retour de CinetPay (return_url pointe vers /orders) :
  // sans ça, une commande reste bloquée en "processing" tant que l'utilisateur ne clique
  // pas manuellement sur "Vérifier le paiement".
  useEffect(() => {
    if (order && order.payment_status === "processing" && !autoVerified) {
      setAutoVerified(true);
      handleVerifyPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, autoVerified]);

  const ORDER_STATUS_MAP = {
    pending: { label: "En attente", class: "badge-warning" },
    confirmed: { label: "Confirmée", class: "badge-info" },
    preparing: { label: "En préparation", class: "badge-info" },
    ready: { label: "Prête", class: "badge-success" },
    shipped: { label: "Expédiée", class: "badge-primary" },
    delivered: { label: "Livrée", class: "badge-success" },
    cancelled: { label: "Annulée", class: "badge-error" },
  };

  const getStatusBadge = (status) => {
    const statusInfo = ORDER_STATUS_MAP[status] || { label: status, class: "badge-primary" };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const statusMap = {
      pending: { label: "En attente de paiement", class: "badge-warning", Icon: Clock },
      processing: { label: "Paiement en cours", class: "badge-info", Icon: RefreshCw },
      success: { label: "Payé", class: "badge-success", Icon: CheckCircle2 },
      failed: { label: "Paiement échoué", class: "badge-error", Icon: XCircle },
      cancelled: { label: "Paiement annulé", class: "badge-error", Icon: Ban },
      refunded: { label: "Remboursé", class: "badge-info", Icon: Banknote },
      refund_pending: { label: "Remboursement en cours", class: "badge-warning", Icon: Clock },
    };
    const statusInfo = statusMap[paymentStatus] || { label: paymentStatus, class: "badge-primary", Icon: null };
    const StatusIcon = statusInfo.Icon;
    return (
      <span className={`badge ${statusInfo.class} icon-inline`}>
        {StatusIcon && <StatusIcon size={13} strokeWidth={2.25} />} {statusInfo.label}
      </span>
    );
  };

  const handleVerifyPayment = async () => {
    setVerifyingPayment(true);
    try {
      await paymentsAPI.verify(order.id);
      await loadOrder(); // Recharger la commande pour avoir les dernières infos
    } catch (err) {
      console.error("Erreur lors de la vérification du paiement:", err);
      setError(err.response?.data?.detail || "Erreur lors de la vérification du paiement. Réessayez dans quelques instants.");
    } finally {
      setVerifyingPayment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifiée";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleStatusUpdateClick = (newStatus) => {
    setConfirmModal({
      isOpen: true,
      action: "updateStatus",
      status: newStatus
    });
  };

  const handleStatusUpdate = async () => {
    const { status } = confirmModal;
    setConfirmModal({ isOpen: false, action: null, status: null });

    try {
      const response = await ordersAPI.update(orderId, { status });
      setOrder(response.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Erreur lors de la mise à jour du statut. Réessayez dans quelques instants.");
    }
  };

  const handleAddTracking = async () => {
    if (!trackingNumber.trim()) {
      setError("Veuillez entrer un numéro de suivi");
      return;
    }
    setAddingTracking(true);
    setError("");
    try {
      const response = await ordersAPI.addTracking(orderId, { tracking_number: trackingNumber });
      setOrder(response.data);
      setTrackingNumber("");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'ajout du numéro de suivi");
    } finally {
      setAddingTracking(false);
    }
  };

  const handleShipOrder = async () => {
    setShipping(true);
    setError("");
    try {
      const trackingData = trackingNumber.trim() ? { tracking_number: trackingNumber } : null;
      const response = await ordersAPI.ship(orderId, trackingData);
      setOrder(response.data);
      setTrackingNumber("");
      // Récupérer l'OTP après l'expédition
      if (response.data.delivery_otp) {
        setDeliveryOTP(response.data.delivery_otp);
        setShowOTP(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'expédition");
    } finally {
      setShipping(false);
    }
  };

  const handleVerifyDelivery = async () => {
    if (!otpCode.trim()) {
      setError("Veuillez entrer le code OTP");
      return;
    }
    
    // Vérification supplémentaire côté frontend
    if (!isRestaurant || user?.role !== "restaurant") {
      setError("Seuls les restaurants peuvent vérifier la livraison");
      return;
    }
    
    if (order.buyer_id !== user.id) {
      setError("Vous ne pouvez vérifier que les commandes que vous avez passées");
      return;
    }
    
    setVerifyingOTP(true);
    setError("");
    try {
      // Log pour déboguer
      console.log("Vérification livraison:", {
        orderId,
        userId: user?.id,
        userEmail: user?.email,
        userRole: user?.role,
        isRestaurant,
        orderBuyerId: order?.buyer_id,
        orderSellerId: order?.seller_id
      });
      const response = await ordersAPI.verifyDelivery(orderId, { otp: otpCode });
      setOrder(response.data);
      setOtpCode("");
    } catch (err) {
      console.error("Erreur vérification livraison:", err.response?.data);
      setError(err.response?.data?.detail || "Code OTP incorrect");
    } finally {
      setVerifyingOTP(false);
    }
  };

  const handleGetOTP = async () => {
    try {
      const response = await ordersAPI.getDeliveryOTP(orderId);
      setDeliveryOTP(response.data.otp);
      setShowOTP(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la récupération de l'OTP");
    }
  };

  const getDeliveryTypeLabel = (type) => {
    const types = {
      breeder: "Livraison par l'éleveur",
      restaurant: "Livraison par le restaurant",
      third_party: "Livraison par un tiers",
      meeting_point: "Point de rencontre",
    };
    return types[type] || type;
  };

  const handleReviewSuccess = async () => {
    setShowReviewForm(false);
    await loadOrder(); // Recharger la commande pour mettre à jour l'état
  };

  if (!isAuthenticated) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">
          Vous devez être connecté pour voir les détails d'une commande
        </div>
        <Link to="/login" className="btn btn-primary">
          Se connecter
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container main-content">
        <div className="empty-state">
          <div className="empty-state-icon"><XCircle size={56} strokeWidth={1.75} /></div>
          <h3>Erreur</h3>
          <p>{error || "Commande non trouvée"}</p>
          <Link to="/orders" className="btn btn-primary">
            Retour aux commandes
          </Link>
        </div>
      </div>
    );
  }

  // Fonction pour obtenir le message d'explication selon le statut et le rôle
  const getStatusExplanation = () => {
    if (isBreeder && order.seller_id === user.id) {
      // Messages pour l'éleveur
      switch (order.status) {
        case "pending":
          if (order.payment_status !== "success") {
            return {
              type: "info",
              Icon: Clock,
              title: "En attente de paiement",
              message: "La commande est en attente. Le restaurant doit effectuer le paiement. Vous serez notifié une fois le paiement confirmé."
            };
          }
          return {
            type: "warning",
            Icon: ClipboardList,
            title: "Action requise",
            message: "Le paiement a été confirmé. Vous devez maintenant confirmer cette commande pour commencer la préparation."
          };
        case "confirmed":
          return {
            type: "info",
            Icon: Package,
            title: "Commande confirmée",
            message: "La commande est confirmée. Vous pouvez maintenant commencer la préparation des produits."
          };
        case "preparing":
          return {
            type: "info",
            Icon: ChefHat,
            title: "En préparation",
            message: "Vous êtes en train de préparer la commande. Passez au statut 'Prête' une fois la préparation terminée."
          };
        case "ready":
          if (order.delivery_type === "breeder" || order.delivery_type === "third_party") {
            if (!order.tracking_number) {
              return {
                type: "warning",
                Icon: Mailbox,
                title: "Action requise : Ajouter un numéro de suivi",
                message: `La commande est prête. Vous devez ajouter un numéro de suivi avant de pouvoir l'expédier.${order.delivery_address ? ` Adresse de livraison : ${order.delivery_address}` : ""}`
              };
            }
            return {
              type: "success",
              Icon: Truck,
              title: "Prête à expédier",
              message: `La commande est prête et le numéro de suivi est enregistré. Vous pouvez maintenant l'expédier à l'adresse : ${order.delivery_address || "Non spécifiée"}. Un code OTP sera généré et envoyé au restaurant.`
            };
          }
          if (order.delivery_type === "restaurant") {
            return {
              type: "success",
              Icon: CheckCircle2,
              title: "Prête",
              message: "La commande est prête. Marquez-la comme expédiée pour générer un code OTP. Le restaurant devra valider ce code lors de la récupération pour que vous receviez le paiement. Ne remettez la marchandise qu'après validation de l'OTP."
            };
          }
          if (order.delivery_type === "meeting_point") {
            return {
              type: "success",
              Icon: MapPin,
              title: "Prête",
              message: `La commande est prête. Marquez-la comme expédiée pour générer un code OTP. Le restaurant devra valider ce code lors de la récupération au point de rencontre${order.meeting_point_address ? ` (${order.meeting_point_address})` : ""} pour que vous receviez le paiement. Ne remettez la marchandise qu'après validation de l'OTP.`
            };
          }
          // Cas par défaut (si delivery_type est null ou autre)
          return {
            type: "success",
            Icon: CheckCircle2,
            title: "Prête",
            message: "La commande est prête. Le restaurant viendra la récupérer ou vous pouvez la marquer comme expédiée."
          };
        case "shipped":
          if (order.delivery_type === "restaurant" || order.delivery_type === "meeting_point") {
            return {
              type: "warning",
              Icon: AlertTriangle,
              title: "Expédiée - En attente de validation OTP",
              message: `La commande a été marquée comme expédiée. Un code OTP a été envoyé au restaurant. IMPORTANT : Ne remettez la marchandise au restaurant que lorsqu'il vous présente et valide le code OTP. Le paiement ne sera libéré qu'après validation de l'OTP par le restaurant.`
            };
          }
          return {
            type: "info",
            Icon: Package,
            title: "Expédiée",
            message: `La commande a été expédiée${order.delivery_address ? ` à l'adresse : ${order.delivery_address}` : ""}. Le restaurant doit confirmer la réception avec le code OTP pour débloquer le paiement.`
          };
        case "delivered":
          if (order.payment_released) {
            return {
              type: "success",
              Icon: Banknote,
              title: "Livraison confirmée et paiement libéré",
              message: "La commande a été livrée et confirmée. Le paiement a été libéré sur votre compte."
            };
          }
          return {
            type: "info",
            Icon: CheckCircle2,
            title: "Livrée",
            message: "La commande a été livrée. En attente de confirmation du restaurant pour libérer le paiement."
          };
        default:
          return null;
      }
    } else if (isRestaurant && order.buyer_id === user.id) {
      // Messages pour le restaurant
      switch (order.status) {
        case "pending":
          if (order.payment_status !== "success") {
            return {
              type: "warning",
              Icon: CreditCard,
              title: "Action requise : Payer la commande",
              message: "Vous devez effectuer le paiement pour confirmer votre commande. Cliquez sur le bouton 'Payer la commande' ci-dessous."
            };
          }
          return {
            type: "info",
            Icon: Clock,
            title: "Paiement effectué - En attente",
            message: "Votre paiement a été confirmé. L'éleveur va maintenant confirmer votre commande et commencer la préparation."
          };
        case "confirmed":
          return {
            type: "info",
            Icon: ClipboardList,
            title: "Commande confirmée",
            message: "L'éleveur a confirmé votre commande. La préparation va commencer prochainement."
          };
        case "preparing":
          return {
            type: "info",
            Icon: ChefHat,
            title: "En préparation",
            message: "L'éleveur est en train de préparer votre commande. Vous serez notifié lorsque la commande sera prête."
          };
        case "ready":
          if (order.delivery_type === "restaurant") {
            return {
              type: "info",
              Icon: Car,
              title: "Commande prête",
              message: `Votre commande est prête. Vous devez aller la récupérer chez l'éleveur${order.seller && order.seller.address ? ` à l'adresse : ${order.seller.address}` : " à l'adresse indiquée"}. Important : Un code OTP vous sera envoyé par email et SMS lorsque l'éleveur marquera la commande comme expédiée. Vous devrez valider ce code pour confirmer la réception et débloquer le paiement à l'éleveur.`
            };
          }
          if (order.delivery_type === "meeting_point") {
            return {
              type: "info",
              Icon: MapPin,
              title: "Commande prête",
              message: `Votre commande est prête. Vous devez aller la récupérer au point de rencontre${order.meeting_point_address ? ` : ${order.meeting_point_address}` : ""}. Important : Un code OTP vous sera envoyé par email et SMS lorsque l'éleveur marquera la commande comme expédiée. Vous devrez valider ce code pour confirmer la réception et débloquer le paiement à l'éleveur.`
            };
          }
          if (order.delivery_type === "breeder" || order.delivery_type === "third_party") {
            return {
              type: "info",
              Icon: Truck,
              title: "Commande prête",
              message: "Votre commande est prête. L'éleveur va bientôt l'expédier. Vous recevrez un code OTP pour confirmer la réception."
            };
          }
          // Cas par défaut
          return {
            type: "info",
            Icon: CheckCircle2,
            title: "Commande prête",
            message: "Votre commande est prête. L'éleveur va bientôt l'expédier ou vous pouvez aller la récupérer selon le type de livraison choisi."
          };
        case "shipped":
          if (!order.delivery_otp_verified) {
            return {
              type: "warning",
              Icon: Package,
              title: "Action requise : Confirmer la réception",
              message: `Votre commande a été expédiée ! Un code OTP a été envoyé à votre email (${order.buyer.email})${order.buyer.phone ? ` et à votre téléphone (${order.buyer.phone})` : ""}. Entrez ce code ci-dessous pour confirmer la réception et débloquer le paiement à l'éleveur.`
            };
          }
          return {
            type: "success",
            Icon: CheckCircle2,
            title: "Réception confirmée",
            message: "Vous avez confirmé la réception. Le paiement sera libéré à l'éleveur."
          };
        case "delivered":
          return {
            type: "success",
            Icon: PartyPopper,
            title: "Commande livrée",
            message: "Votre commande a été livrée avec succès. N'hésitez pas à laisser un avis sur l'éleveur !"
          };
        default:
          return null;
      }
    }
    return null;
  };

  const statusExplanation = getStatusExplanation();

  // Fonction pour déterminer s'il y a des actions disponibles
  const hasActions = () => {
    if (!order || !user) return false;
    
    // Pour les éleveurs
    if (isBreeder && order.seller_id === user.id && order.status !== "cancelled" && order.status !== "delivered") {
      // Actions disponibles si :
      // - Statut pending avec paiement success (peut confirmer)
      if (order.status === "pending" && order.payment_status === "success") return true;
      // - Statut confirmed (peut passer en préparation)
      if (order.status === "confirmed") return true;
      // - Statut preparing (peut passer en ready)
      if (order.status === "preparing") return true;
      // - Statut ready/confirmed/preparing avec paiement success (peut expédier)
      if ((order.status === "ready" || order.status === "confirmed" || order.status === "preparing") && 
          order.payment_status === "success") return true;
      return false;
    }
    
    // Pour les restaurants
    if (isRestaurant && user?.role === "restaurant" && order.buyer_id === user.id && order.status !== "cancelled") {
      // Actions disponibles si :
      // - Statut non delivered (peut annuler)
      if (order.status !== "delivered") return true;
      // - Statut shipped avec OTP non vérifié (peut vérifier)
      if (order.status === "shipped" && !order.delivery_otp_verified) return true;
      return false;
    }
    
    return false;
  };

  const hasAvailableActions = hasActions();

  return (
    <div className="container main-content">
      <div className="page-header">
        <Link to="/orders" className="btn btn-outline btn-sm" style={{ marginBottom: "1rem" }}>
          <ArrowLeft size={14} strokeWidth={2.25} /> Retour aux commandes
        </Link>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <h1>Commande #{order.id}</h1>
            <p className="order-date">Passée le {formatDate(order.created_at)}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
            {getStatusBadge(order.status)}
            {order.payment_status && getPaymentStatusBadge(order.payment_status)}
          </div>
        </div>
      </div>

      {/* Message d'explication du statut */}
      {statusExplanation && (
        <div className={`alert alert-${statusExplanation.type}`} style={{ 
          marginBottom: "1.5rem",
          padding: "1.25rem",
          borderRadius: "var(--radius-md)",
          borderLeft: `4px solid ${
            statusExplanation.type === "warning" ? "var(--warning)" :
            statusExplanation.type === "success" ? "var(--success)" :
            "var(--info)"
          }`
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
            <span style={{ flexShrink: 0 }}><statusExplanation.Icon size={32} strokeWidth={1.75} /></span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: "0 0 0.5rem 0", fontSize: "1.1rem", fontWeight: 600 }}>
                {statusExplanation.title}
              </h3>
              <p style={{ margin: 0, fontSize: "0.95rem", lineHeight: 1.6 }}>
                {statusExplanation.message}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="order-detail-grid">
        {/* Informations produit */}
        <div className="card order-detail-section">
          <h2 className="section-title">Produit commandé</h2>
          <div className="order-product-detail">
            <Link
              to={`/products/${order.product.id}`}
              className="order-product-link"
            >
              <strong>{order.product.name}</strong>
            </Link>
            <div className="product-info-grid">
              <div>
                <span className="info-label">Quantité:</span>
                <span className="info-value">{order.quantity} {order.product.unit}</span>
              </div>
              <div>
                <span className="info-label">Prix unitaire:</span>
                <span className="info-value">
                  {order.product.unit === "lot" && order.product.lot_price
                    ? `${order.product.lot_price.toLocaleString("fr-FR")} F CFA / lot`
                    : `${order.product.price.toLocaleString("fr-FR")} F CFA / ${order.product.unit}`}
                </span>
              </div>
              <div>
                <span className="info-label">Catégorie:</span>
                <span className="info-value">{order.product.category || "Non spécifiée"}</span>
              </div>
              {order.product.certification && (
                <div>
                  <span className="info-label">Certification:</span>
                  <span className="info-value">{order.product.certification}</span>
                </div>
              )}
            </div>
            {order.product.description && (
              <div className="product-description">
                <p>{order.product.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Informations parties */}
        <div className="card order-detail-section">
          <h2 className="section-title">Parties</h2>
          <div className="parties-info">
            <div className="party-card">
              <h3>Vendeur</h3>
              <p><strong>{order.seller.name}</strong></p>
              {order.seller.city && <p className="icon-inline"><MapPin size={14} strokeWidth={2.25} /> {order.seller.city}</p>}
              {order.seller.phone && <p className="icon-inline"><Phone size={14} strokeWidth={2.25} /> {order.seller.phone}</p>}
              {order.seller.email && <p className="icon-inline"><Mail size={14} strokeWidth={2.25} /> {order.seller.email}</p>}
            </div>
            <div className="party-card">
              <h3>Acheteur</h3>
              <p><strong>{order.buyer.name}</strong></p>
              {order.buyer.city && <p className="icon-inline"><MapPin size={14} strokeWidth={2.25} /> {order.buyer.city}</p>}
              {order.buyer.phone && <p className="icon-inline"><Phone size={14} strokeWidth={2.25} /> {order.buyer.phone}</p>}
              {order.buyer.email && <p className="icon-inline"><Mail size={14} strokeWidth={2.25} /> {order.buyer.email}</p>}
            </div>
          </div>
        </div>

        {/* Informations livraison */}
        <div className="card order-detail-section">
          <h2 className="section-title">Livraison</h2>
          <div className="delivery-info">
            {order.delivery_type && (
              <div style={{ marginBottom: "1rem" }}>
                <p><strong>Type de livraison:</strong></p>
                <p>{getDeliveryTypeLabel(order.delivery_type)}</p>
              </div>
            )}
            {order.delivery_address && (
              <div style={{ 
                marginBottom: "1rem", 
                padding: "1rem", 
                background: (order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                  ? "rgba(255, 193, 7, 0.1)" 
                  : "transparent",
                borderRadius: "var(--radius-sm)",
                border: (order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                  ? "2px solid #ffc107"
                  : "none"
              }}>
                <p><strong className="icon-inline" style={{ fontSize: "1.1rem" }}>
                  <MapPin size={16} strokeWidth={2.25} />
                  {(order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                    ? "Adresse de livraison (à livrer) :"
                    : "Adresse de livraison :"}
                </strong></p>
                <LocationDisplay address={order.delivery_address} />
                {(order.delivery_type === "breeder" || order.delivery_type === "third_party") && (order.status === "ready" || order.status === "shipped" || order.status === "preparing") && (
                  <p style={{ 
                    marginTop: "0.5rem", 
                    fontSize: "0.85rem", 
                    color: "#856404",
                    fontStyle: "italic"
                  }}>
                    Cette adresse doit être utilisée pour la livraison de la commande
                  </p>
                )}
              </div>
            )}
            {order.meeting_point_address && (
              <div style={{ 
                marginBottom: "1rem", 
                padding: "1rem", 
                background: order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                  ? "rgba(33, 150, 243, 0.1)" 
                  : "transparent",
                borderRadius: "var(--radius-sm)",
                border: order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                  ? "2px solid #2196F3"
                  : "none"
              }}>
                <p><strong className="icon-inline" style={{ fontSize: "1.1rem" }}>
                  <MapPin size={16} strokeWidth={2.25} />
                  {order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                    ? "Point de rencontre (à récupérer) :"
                    : "Point de rencontre :"}
                </strong></p>
                <LocationDisplay address={order.meeting_point_address} />
                {order.delivery_type === "meeting_point" && (order.status === "ready" || order.status === "shipped" || order.status === "preparing") && (
                  <p style={{ 
                    marginTop: "0.5rem", 
                    fontSize: "0.85rem", 
                    color: "#1976D2",
                    fontStyle: "italic"
                  }}>
                    Rendez-vous à cette adresse pour récupérer votre commande
                  </p>
                )}
              </div>
            )}
            {/* Adresse de l'éleveur pour livraison par le restaurant */}
            {order.delivery_type === "restaurant" && order.seller && order.seller.address && (
              <div style={{ 
                marginBottom: "1rem", 
                padding: "1rem", 
                background: (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                  ? "rgba(33, 150, 243, 0.1)" 
                  : "transparent",
                borderRadius: "var(--radius-sm)",
                border: (order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                  ? "2px solid #2196F3"
                  : "none"
              }}>
                <p><strong className="icon-inline" style={{ fontSize: "1.1rem" }}>
                  <MapPin size={16} strokeWidth={2.25} />
                  {(order.status === "ready" || order.status === "shipped" || order.status === "preparing")
                    ? "Adresse de l'éleveur (à récupérer) :"
                    : "Adresse de l'éleveur :"}
                </strong></p>
                <LocationDisplay address={order.seller.address} />
                {(order.status === "ready" || order.status === "shipped" || order.status === "preparing") && (
                  <p style={{ 
                    marginTop: "0.5rem", 
                    fontSize: "0.85rem", 
                    color: "#1976D2",
                    fontStyle: "italic"
                  }}>
                    Rendez-vous à cette adresse pour récupérer votre commande
                  </p>
                )}
              </div>
            )}
            {order.tracking_number && (
              <div style={{ marginBottom: "1rem" }}>
                <p><strong>Numéro de suivi:</strong></p>
                <p style={{ fontFamily: "monospace", fontSize: "1.1rem", fontWeight: "bold" }}>
                  {order.tracking_number}
                </p>
              </div>
            )}
            {order.delivery_otp_verified && (
              <div style={{ marginBottom: "1rem" }}>
                <p className="badge badge-success icon-inline"><CheckCircle2 size={13} strokeWidth={2.25} /> Livraison vérifiée</p>
              </div>
            )}
            {order.payment_released && (
              <div style={{ marginBottom: "1rem" }}>
                <p className="badge badge-success icon-inline"><Banknote size={13} strokeWidth={2.25} /> Paiement libéré</p>
              </div>
            )}
            {order.delivery_date && (
              <div style={{ marginTop: "1rem" }}>
                <p><strong>Date de livraison prévue:</strong></p>
                <p>{formatDate(order.delivery_date)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="card order-detail-section">
            <h2 className="section-title">Notes</h2>
            <p>{order.notes}</p>
          </div>
        )}

        {/* Résumé financier */}
        <div className="card order-detail-section order-summary" data-tour="order-summary">
          <h2 className="section-title">Résumé</h2>
          <div className="summary-details">
            <div className="summary-row">
              <span>Sous-total:</span>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {order.product.unit === "lot" && order.product.lot_price
                  ? (order.product.lot_price * order.quantity).toLocaleString("fr-FR")
                  : (order.product.price * order.quantity).toLocaleString("fr-FR")} F CFA
              </span>
            </div>
            {order.delivery_price > 0 && (
              <div className="summary-row">
                <span>Frais de livraison:</span>
                <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                  {order.delivery_price.toLocaleString("fr-FR")} F CFA
                </span>
              </div>
            )}
            <div className="summary-row summary-total">
              <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "var(--primary)" }}>Total:</span>
              <span style={{ fontWeight: 700, fontSize: "1.3rem", color: "var(--primary)" }}>
                {order.total_price.toLocaleString("fr-FR")} F CFA
              </span>
            </div>
          </div>
          
          {/* Section paiement */}
          {isRestaurant && order.buyer_id === user.id && (
            <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: "1px solid var(--border-light)" }} data-tour="order-payment">
              {order.payment_status === "success" ? (
                <div className="alert alert-success icon-inline">
                  <CheckCircle2 size={15} strokeWidth={2.25} /> Paiement effectué avec succès
                </div>
              ) : order.payment_status === "processing" ? (
                <div>
                  <div className="alert alert-info icon-inline" style={{ marginBottom: "1rem" }}>
                    <RefreshCw size={15} strokeWidth={2.25} /> Paiement en cours de traitement
                  </div>
                  <button
                    onClick={handleVerifyPayment}
                    className="btn btn-outline"
                    disabled={verifyingPayment}
                    style={{ width: "100%" }}
                  >
                    {verifyingPayment ? "Vérification..." : "Vérifier le paiement"}
                  </button>
                </div>
              ) : (
                <div>
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="btn btn-primary"
                    style={{ width: "100%" }}
                  >
                    <CreditCard size={16} strokeWidth={2.25} /> Payer la commande
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions pour l'éleveur */}
      {isBreeder && order.seller_id === user.id && order.status !== "cancelled" && order.status !== "delivered" && (
        <div className={`card order-actions ${hasAvailableActions ? 'order-actions-animated' : ''}`} data-tour="order-actions-breeder">
          <h2 className="section-title">Actions</h2>
          {(order.status === "confirmed" || order.status === "preparing") && (
            <Hint id="order_actions_flexibility">
              Vous pouvez suivre les étapes une par une (<strong>En préparation</strong> puis <strong>Prête à expédier</strong>),
              ou passer directement à l'expédition si votre produit est déjà prêt.
            </Hint>
          )}
          <div className="actions-buttons">
            {order.status === "pending" && (
              <button
                onClick={() => handleStatusUpdateClick("confirmed")}
                className="btn btn-primary"
              >
                Confirmer la commande
              </button>
            )}
            {order.status === "confirmed" && (
              <button
                onClick={() => handleStatusUpdateClick("preparing")}
                className="btn btn-primary"
              >
                En préparation
              </button>
            )}
            {order.status === "preparing" && (
              <button
                onClick={() => handleStatusUpdateClick("ready")}
                className="btn btn-primary"
              >
                Prête à expédier
              </button>
            )}
            {(order.status === "ready" || order.status === "confirmed" || order.status === "preparing") && 
             order.payment_status === "success" && 
             (order.delivery_type === "breeder" || order.delivery_type === "third_party") && (
              <div style={{ width: "100%", marginTop: "1rem" }}>
                {order.delivery_address && (
                  <div style={{ 
                    marginBottom: "1rem", 
                    padding: "1rem", 
                    background: "rgba(33, 150, 243, 0.1)", 
                    borderRadius: "var(--radius-md)",
                    border: "2px solid #2196F3"
                  }}>
                    <p className="icon-inline" style={{ margin: "0 0 0.5rem 0", fontWeight: "bold", color: "#1976D2", fontSize: "1rem" }}>
                      <MapPin size={15} strokeWidth={2.25} /> Adresse de livraison :
                    </p>
                    <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600, color: "var(--text-primary)" }}>
                      {order.delivery_address}
                    </p>
                    <p className="icon-inline" style={{ margin: "0.5rem 0 0 0", fontSize: "0.85rem", color: "#1976D2", fontStyle: "italic" }}>
                      <AlertTriangle size={13} strokeWidth={2.25} /> Assurez-vous de livrer la commande à cette adresse exacte
                    </p>
                  </div>
                )}
                {!order.tracking_number && (
                  <div style={{ marginBottom: "1rem" }}>
                    <label className="form-label">Numéro de suivi *</label>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <input
                        type="text"
                        className="form-control"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Entrez le numéro de suivi"
                        style={{ flex: 1 }}
                      />
                      <button
                        onClick={handleAddTracking}
                        className="btn btn-outline"
                        disabled={addingTracking || !trackingNumber.trim()}
                      >
                        {addingTracking ? "..." : "Ajouter"}
                      </button>
                    </div>
                  </div>
                )}
                {order.tracking_number && (
                  <div style={{ marginBottom: "1rem", padding: "1rem", background: "var(--background)", borderRadius: "var(--radius-md)" }}>
                    <p><strong>Numéro de suivi:</strong> {order.tracking_number}</p>
                  </div>
                )}
                <button
                  onClick={handleShipOrder}
                  className="btn btn-primary"
                  disabled={shipping || (order.delivery_type !== "meeting_point" && !order.tracking_number)}
                  style={{ width: "100%" }}
                >
                  {shipping ? "Expédition..." : "Expédier la commande"}
                </button>
                {showOTP && deliveryOTP && (
                  <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--success-light)", borderRadius: "var(--radius-md)", border: "2px solid var(--success)" }}>
                    <p className="icon-inline" style={{ margin: "0 0 0.5rem 0", fontWeight: "bold" }}><Package size={15} strokeWidth={2.25} /> Code OTP de livraison:</p>
                    <p style={{ margin: 0, fontSize: "1.5rem", fontFamily: "monospace", fontWeight: "bold", textAlign: "center" }}>
                      {deliveryOTP}
                    </p>
                    {(order.delivery_type === "restaurant" || order.delivery_type === "meeting_point") ? (
                      <div style={{ marginTop: "0.5rem", padding: "0.75rem", background: "rgba(255, 193, 7, 0.2)", borderRadius: "var(--radius-sm)", borderLeft: "3px solid #ffc107" }}>
                        <p className="icon-inline" style={{ margin: "0", fontSize: "0.85rem", fontWeight: "bold", color: "#856404" }}>
                          <AlertTriangle size={14} strokeWidth={2.25} /> IMPORTANT : Ne remettez la marchandise au restaurant que lorsqu'il vous présente et valide ce code OTP dans l'application. Le paiement ne sera libéré qu'après validation de l'OTP.
                        </p>
                      </div>
                    ) : (
                      <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                        Ce code a été envoyé par email et SMS au restaurant. Le restaurant doit l'entrer dans l'application pour confirmer la réception et débloquer le paiement.
                      </p>
                    )}
                  </div>
                )}
                {order.status === "shipped" && !showOTP && (
                  <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--background)", borderRadius: "var(--radius-md)" }}>
                    <p className="icon-inline" style={{ margin: "0 0 0.5rem 0", fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                      <CheckCircle2 size={14} strokeWidth={2.25} /> Le code OTP a été envoyé par email et SMS à l'acheteur.
                    </p>
                    {(order.delivery_type === "restaurant" || order.delivery_type === "meeting_point") && (
                      <div style={{ marginTop: "0.5rem", padding: "0.75rem", background: "rgba(255, 193, 7, 0.2)", borderRadius: "var(--radius-sm)", borderLeft: "3px solid #ffc107" }}>
                        <p className="icon-inline" style={{ margin: "0", fontSize: "0.85rem", fontWeight: "bold", color: "#856404" }}>
                          <AlertTriangle size={14} strokeWidth={2.25} /> Ne remettez la marchandise qu'après validation de l'OTP par le restaurant.
                        </p>
                      </div>
                    )}
                    <button
                      onClick={handleGetOTP}
                      className="btn btn-outline"
                      style={{ width: "100%", marginTop: "0.5rem" }}
                    >
                      Voir le code OTP (pour référence)
                    </button>
                  </div>
                )}
              </div>
            )}
            {(order.status === "ready" || order.status === "confirmed" || order.status === "preparing") && 
             order.payment_status === "success" && 
             (order.delivery_type === "restaurant" || order.delivery_type === "meeting_point") && (
              <button
                onClick={handleShipOrder}
                className="btn btn-primary"
                disabled={shipping}
                style={{ width: "100%" }}
              >
                {shipping ? "Expédition..." : "Marquer comme expédiée"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Actions pour le restaurateur */}
      {/* Vérification stricte : doit être un restaurant ET être l'acheteur de la commande */}
      {isRestaurant && user?.role === "restaurant" && order.buyer_id === user.id && order.status !== "cancelled" && (
        <div className={`card order-actions ${hasAvailableActions ? 'order-actions-animated' : ''}`} data-tour="order-actions-restaurant">
          <h2 className="section-title">Actions</h2>
          <div className="actions-buttons">
            {order.status !== "delivered" && (
              <button
                onClick={() => handleStatusUpdateClick("cancelled")}
                className="btn btn-error"
              >
                Annuler la commande
              </button>
            )}
            {order.status === "shipped" && !order.delivery_otp_verified && (
              <div style={{ width: "100%", marginTop: "1rem" }}>
                <div style={{ marginBottom: "1rem", padding: "1rem", background: "linear-gradient(135deg, rgba(23, 162, 184, 0.1) 0%, rgba(74, 144, 226, 0.1) 100%)", borderRadius: "var(--radius-md)", borderLeft: "4px solid var(--info)" }}>
                  <p className="icon-inline" style={{ margin: "0 0 0.5rem 0", fontWeight: "bold", fontSize: "1rem" }}><Package size={15} strokeWidth={2.25} /> Vérification de livraison</p>
                  <p style={{ margin: "0.5rem 0", fontSize: "0.9rem" }}>
                    {(order.delivery_type === "restaurant" || order.delivery_type === "meeting_point") ? (
                      <>
                        Votre commande est prête à être récupérée ! Un code OTP a été envoyé à votre adresse email <strong>{order.buyer.email}</strong>
                        {order.buyer.phone && ` et à votre numéro de téléphone ${order.buyer.phone}`}.
                        <br /><br />
                        <strong className="icon-inline"><AlertTriangle size={13} strokeWidth={2.25} /> IMPORTANT :</strong> Vous devez présenter ce code OTP à l'éleveur lors de la récupération de la marchandise. L'éleveur ne doit vous remettre la marchandise qu'après validation de ce code dans l'application.
                      </>
                    ) : (
                      <>
                        Votre commande a été expédiée ! Un code OTP a été envoyé à votre adresse email <strong>{order.buyer.email}</strong>
                        {order.buyer.phone && ` et à votre numéro de téléphone ${order.buyer.phone}`}.
                      </>
                    )}
                  </p>
                  <p className="icon-inline" style={{ margin: "0.5rem 0 0 0", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                    <AlertTriangle size={13} strokeWidth={2.25} /> <strong>Important:</strong> Entrez ce code ci-dessous pour confirmer la réception et débloquer le paiement à l'éleveur. Le paiement ne sera libéré qu'après validation de l'OTP.
                  </p>
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <label className="form-label">Code OTP *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="Entrez le code OTP"
                    maxLength="6"
                    style={{ fontFamily: "monospace", fontSize: "1.2rem", textAlign: "center", letterSpacing: "0.5rem" }}
                  />
                </div>
                <button
                  onClick={handleVerifyDelivery}
                  className="btn btn-success"
                  disabled={verifyingOTP || !otpCode.trim()}
                  style={{ width: "100%" }}
                >
                  {verifyingOTP ? "Vérification..." : "Confirmer la réception"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section avis - Uniquement pour les restaurants qui ont reçu la commande */}
      {isRestaurant && 
       user?.role === "restaurant" && 
       order.buyer_id === user.id && 
       order.status === "delivered" && (
        <div className="card" style={{ marginTop: "1rem" }} data-tour="order-review">
          <h2 className="section-title">Avis</h2>
          {existingReview ? (
            <div className="alert alert-success">
              <p><strong className="icon-inline"><CheckCircle2 size={15} strokeWidth={2.25} /> Vous avez déjà laissé un avis pour cette commande</strong></p>
              <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--background)", borderRadius: "var(--radius-sm)" }}>
                <div style={{ display: "flex", gap: "0.25rem", marginBottom: "0.5rem" }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={19}
                      strokeWidth={1.75}
                      color="var(--warning)"
                      fill={star <= existingReview.rating ? "var(--warning)" : "none"}
                    />
                  ))}
                </div>
                {existingReview.comment && (
                  <p style={{ margin: "0.5rem 0 0 0", color: "var(--text-primary)" }}>
                    {existingReview.comment}
                  </p>
                )}
              </div>
            </div>
          ) : showReviewForm ? (
            <ReviewForm
              orderId={order.id}
              reviewedUserId={order.seller_id}
              onSuccess={handleReviewSuccess}
              onCancel={() => setShowReviewForm(false)}
            />
          ) : (
            <div>
              <p style={{ marginBottom: "1rem", color: "var(--text-secondary)" }}>
                Partagez votre expérience avec cet éleveur en laissant un avis.
              </p>
              <button
                onClick={() => setShowReviewForm(true)}
                className="btn btn-primary"
              >
<Star size={16} strokeWidth={2.25} /> Laisser un avis
              </button>
            </div>
          )}
        </div>
      )}

      {/* Lien vers les messages - Uniquement pour les restaurants (jamais pour les éleveurs) */}
      {(() => {
        // Vérification stricte : uniquement pour les restaurants qui sont acheteurs
        const shouldShowContact = user?.role === "restaurant" && 
                                  order.buyer_id === user.id && 
                                  user.role !== "breeder";
        
        // Log pour déboguer
        if (user && order) {
          console.log("Contact button check:", {
            userRole: user.role,
            isBreeder: isBreeder,
            isRestaurant: isRestaurant,
            buyerId: order.buyer_id,
            userId: user.id,
            shouldShow: shouldShowContact
          });
        }
        
        return shouldShowContact ? (
          <div className="card" style={{ marginTop: "1rem", textAlign: "center" }}>
            <Link
              to={`/messages?user_id=${order.seller.id}&order_id=${order.id}`}
              className="btn btn-outline"
            >
<MessageCircle size={16} strokeWidth={2.25} /> Contacter {order.seller.name}
            </Link>
          </div>
        ) : null;
      })()}

      {/* Modal de paiement */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        order={order}
        onPaymentSuccess={() => {
          setShowPaymentModal(false);
          loadOrder();
        }}
      />

      {/* Modal de confirmation */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, status: null })}
        onConfirm={handleStatusUpdate}
        title={confirmModal.status === "cancelled" ? "Annuler la commande" : "Mettre à jour le statut"}
        message={
          confirmModal.status === "cancelled"
            ? `Êtes-vous sûr de vouloir annuler cette commande ?${order?.payment_status === "success" ? " Le restaurateur sera remboursé automatiquement." : ""}`
            : `Voulez-vous marquer cette commande comme « ${ORDER_STATUS_MAP[confirmModal.status]?.label || confirmModal.status} » ?`
        }
        confirmText={confirmModal.status === "cancelled" ? "Annuler" : "Confirmer"}
        cancelText="Retour"
        type={confirmModal.status === "cancelled" ? "danger" : "warning"}
      />
    </div>
  );
};

export default OrderDetail;

